import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import CLocalizedText from '../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CPageModule from '../../components/CPageModule';
import LinkTabButton from '../../components/LinkTabButton';
import LinkTabButtonWrapper from '../../components/LinkTabButtonWrapper';
import SingleDateSelector from '../../components/SingleDateSelector';
import PageOutlet from '../../components/layout/PageOutlet';
import { operationsSections } from '../../constants/operationsSections';
import { type EPortfolioManagementTabs } from '../../enums/EPortfolioManagementTabs';
import { PageRoutes } from '../../enums/enums';
import HGeneric from '../../helpers/HGeneric';
import { updateStateAttribute, updateStateResponseStatefulAttribute } from '../../helpers/helper';
import { useAppSelector } from '../../hooks/hooks';
import type { CachedAPIResponse } from '../../models/CachedAPIResponse';
import type { IFundTabs } from '../../models/IFundTabs';
import type { IResponse } from '../../models/IResponse';
import { SvMultifundFactsheetService } from '../../services/SvMultifundFactsheetService';
import { SvPortfolioManagementServices } from '../../services/SvPortfolioManagementServices';
import PortfolioManagementsStickyHeader from './layout/PortfolioManagementsStickyHeader';

interface State {
  targetScrollProgress?: number;
  isHedgeRoute: boolean;
  asOfDefaultMs: number;
  hedgeMissingDates: IResponse<CachedAPIResponse<{ missing_dates: number[] }>>;
  tabs: IResponse<IFundTabs>;
}

export interface PortfolioManagementOutletContext {
  factsheetId: string;
  tabs: EPortfolioManagementTabs[];
  asOfDateTsMs: number;
  handleAsOfDateTsMsChange: (asOfDateTsMs: number, replaceDefault?: boolean) => void;
  isAsOfExplicit: boolean;
  hedgeMinDate: number;
  nowTimestamp: number;
  shouldDisableHedgeAsOfDate?: (date: Dayjs) => boolean;
}

const MIN_DATE_TS_MS = dayjs('2025-12-17').valueOf();
const MAX_DATE_TS_MS = dayjs().valueOf();

export default function PPortfolioManagementOutlet() {
  const navigate = useNavigate();
  const location = useLocation();
  const { factsheetId = '' } = useParams<{ factsheetId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const targetRef = useRef<HTMLDivElement>(null);
  const { dashboard } = useAppSelector((state) => state);
  const fundMeta = dashboard.funds.find((e) => e.id === factsheetId);

  const [state, setState] = useState<State>({
    isHedgeRoute: false,
    targetScrollProgress: undefined,
    asOfDefaultMs: new Date().getTime(),
    hedgeMissingDates: {
      state: {
        isLoading: true,
        isError: false
      }
    },
    tabs: {
      state: {
        isLoading: false,
        isError: false
      }
    }
  });
  const portfolioManagementTitle = CLocalizedTextStringStatic('globalSidebarOpsNews');
  const dateLabel = CLocalizedTextStringStatic('globalSingleDatelabel');
  const pageTitle = [fundMeta?.shortName, CLocalizedTextStringStatic('opsP1H1')].filter(Boolean).join(' ');

  const asOfParam = searchParams.get('asOf');
  const isAsOfExplicit = !!asOfParam;

  const asOfDateTsMsRaw = Number(asOfParam);
  const asOfDateTsMs = asOfDateTsMsRaw > 0 ? asOfDateTsMsRaw : state.asOfDefaultMs;

  const currentSection = location.pathname.split('/').pop();

  const fetchTabList = async () => {
    updateStateAttribute<State>(
      {
        tabs: {
          ...state.tabs,
          state: {
            ...state.tabs.state,
            isLoading: true
          }
        }
      },
      setState
    );

    const res = await SvMultifundFactsheetService.getRelevantTabs(factsheetId);

    updateStateAttribute<State>(
      {
        tabs: {
          data: await res.getData(),
          state: {
            isLoading: false,
            isError: !(await res.isSuccess())
          }
        }
      },
      setState
    );

    return await res.getData();
  };

  useEffect(() => {
    if (!factsheetId) return;

    (async () => {
      const tabList = ((await fetchTabList())?.portfolioManagementTabs as string[]) ?? [];

      const lastPathSlug = location.pathname.split('/').pop() ?? '';
      if (tabList.length > 0 && !tabList.includes(lastPathSlug)) {
        navigate(
          {
            pathname: `/${PageRoutes.OPS_NEWS_OUTLET}/${factsheetId}/${tabList[0]}`,
            search: location.search
          },
          { replace: true }
        );
      }

      updateStateAttribute<State>(
        {
          isHedgeRoute:
            location.pathname === `/${PageRoutes.OPS_NEWS_OUTLET}/${factsheetId}/${PageRoutes.OPS_NEWS_HEDGE}`
        },
        setState
      );
    })();
  }, [factsheetId]);

  useEffect(() => {
    if (!asOfParam) return;

    let nextAsOf = Number(asOfParam);

    if (!nextAsOf || isNaN(nextAsOf)) nextAsOf = MAX_DATE_TS_MS;
    if (nextAsOf < MIN_DATE_TS_MS) nextAsOf = MIN_DATE_TS_MS;
    if (nextAsOf > MAX_DATE_TS_MS) nextAsOf = MAX_DATE_TS_MS;

    const next = new URLSearchParams(searchParams);

    if (HGeneric.isSameDayInLocalTimezone([new Date(nextAsOf), new Date(state.asOfDefaultMs)])) {
      next.delete('asOf');
    } else {
      next.set('asOf', String(nextAsOf));
    }

    if (searchParams.toString() !== next.toString()) {
      navigate(
        {
          pathname: location.pathname,
          search: next.toString()
        },
        { replace: true }
      );
    }
  }, [location.pathname, asOfParam, navigate, searchParams]);

  const handleAsOfDateTsMsChange = (ts: number, replaceDefault?: boolean) => {
    if (replaceDefault) {
      updateStateAttribute({ asOfDefaultMs: ts }, setState);
    }

    const next = new URLSearchParams(searchParams);

    if (HGeneric.isSameDayInLocalTimezone([new Date(ts), new Date(state.asOfDefaultMs)])) {
      next.delete('asOf');
    } else {
      next.set('asOf', String(ts));
    }

    setSearchParams(next, { replace: true });
  };

  const hasPortfolioManagementTabs = (state.tabs.data?.portfolioManagementTabs.length ?? 0) > 0;

  const missingLocalDays = new Set(
    (state.hedgeMissingDates.data?.cacheData.missing_dates ?? []).map((sec) => HGeneric.localDayKey(sec * 1000))
  );

  const shouldDisableHedgeAsOfDate = (d: Dayjs) => {
    if (!d?.isValid()) return false;

    return missingLocalDays.has(d.format('YYYY-MM-DD'));
  };

  const renderTabButtons = () =>
    state.tabs.data?.portfolioManagementTabs.map((section) => {
      const config = operationsSections[section satisfies EPortfolioManagementTabs];

      return (
        <LinkTabButton
          key={section}
          to={{
            pathname: `/${PageRoutes.OPS_NEWS_OUTLET}/${factsheetId}/${config.path}`,
            search: location.search
          }}
          label={config.dictKey}
          text={<CLocalizedText dictKey={config.dictKey} />}
          isSelected={currentSection === config.path}
        />
      );
    });

  const stickyHeader = hasPortfolioManagementTabs ? (
    <PortfolioManagementsStickyHeader
      factsheetId={factsheetId}
      tabs={state.tabs.data?.portfolioManagementTabs ?? []}
      currentTab={currentSection as EPortfolioManagementTabs}
    >
      {state.isHedgeRoute && (
        <div className={'flex justify-start'}>
          <SingleDateSelector
            label={dateLabel}
            minDate={MIN_DATE_TS_MS}
            maxDate={MAX_DATE_TS_MS}
            limitWidth
            initDate={asOfDateTsMs}
            shouldDisableDate={shouldDisableHedgeAsOfDate}
            onChange={handleAsOfDateTsMsChange}
          />
        </div>
      )}
    </PortfolioManagementsStickyHeader>
  ) : null;

  useEffect(() => {
    const progress = targetRef.current?.getBoundingClientRect().top ?? 0;

    if (progress > 0) {
      updateStateAttribute(
        {
          targetScrollProgress: progress
        },
        setState
      );
    }
  }, [dashboard.dashboardOutletDimensions.w]);

  useEffect(() => {
    if (!state.isHedgeRoute) return;

    void (async () => {
      await updateStateResponseStatefulAttribute<State, typeof state.hedgeMissingDates.data>(
        {
          hedgeMissingDates: state.hedgeMissingDates
        },
        setState,
        async () =>
          await SvPortfolioManagementServices.getHedgeMissingDates({
            factsheetID: factsheetId
          })
      );
    })();
  }, [state.isHedgeRoute, factsheetId]);

  return (
    <PageOutlet
      outletKey={PageRoutes.OPS_NEWS_OUTLET}
      title={portfolioManagementTitle}
      outletContext={
        {
          factsheetId,
          tabs: state.tabs.data?.portfolioManagementTabs ?? [],
          asOfDateTsMs,
          handleAsOfDateTsMsChange,
          isAsOfExplicit,
          hedgeMinDate: MIN_DATE_TS_MS,
          nowTimestamp: MAX_DATE_TS_MS,
          shouldDisableHedgeAsOfDate
        } satisfies PortfolioManagementOutletContext
      }
      stickyHeaderConfig={
        hasPortfolioManagementTabs && state.targetScrollProgress
          ? {
              content: stickyHeader,
              targetScrollProgress: state.targetScrollProgress,
              pad: true
            }
          : undefined
      }
    >
      <CPageModule fullWidth style={{ paddingBottom: 0 }}>
        <CPageModule submodule>
          <h1>{pageTitle}</h1>
          <CLocalizedText dictKey={'opsP1Desc'} />
        </CPageModule>

        <div ref={targetRef}>
          <LinkTabButtonWrapper id={'pm-sections'}>{renderTabButtons()}</LinkTabButtonWrapper>
        </div>
      </CPageModule>

      <CPageModule style={{ paddingBottom: 0 }} />
    </PageOutlet>
  );
}
